import ApiModelWithAuth from "../../../models/ApiModelWithAuth";
import Formatter from "../../../models/Formatter";

export class ProxyFormatter extends Formatter {
    // expires_at — «оплачен до», по смыслу дата: бэкенд хранит полночь UTC. Держим строкой
    // YYYY-MM-DD — так её ждёт поле даты, и diff формы не видит изменения там, где его нет.
    static CustomItem(data) {
        return {
            expires_at: data.expires_at ? String(data.expires_at).slice(0, 10) : '',
        };
    }
}

// Справочник прокси + проверка (TCP/UDP через прокси до хаба). Дока — api/docs/API-FRONT-PROXIES.md.
// Пароль сервер не отдаёт никогда (только has_password); на update: не передан — прежний,
// null — стереть. proxy_group_id — группа прокси (§8), null — без группы.
export default class ApiProxy extends ApiModelWithAuth {
    pathDef = 'authorized/admin/proxies';
    formatterItem = ProxyFormatter.Item;

    // ProxyFilter читает сортировку как order_by / order_direction, а List шлёт orderBy /
    // orderBySort — без перевода направление терялось бы и список всегда шёл бы по убыванию.
    static Index(params = {}) {
        const { orderBy, orderBySort, ...other } = params;
        return super.Index({ ...other, order_by: orderBy, order_direction: orderBySort });
    }

    // Флаг формы «стереть пароль» → password: null. Отдельным флагом, а не пустым полем:
    // пустое поле значит «не трогали», и diff формы его не отправит.
    getParamsForUpdate(params) {
        const { is_password_clear: isPasswordClear, ...other } = params;
        return isPasswordClear ? { ...other, password: null } : other;
    }

    // Импорт списком (POST proxies/import): list — текст, по прокси на строку. Импорт частичный,
    // ответ всегда 200: {data: [заведённые], skipped: [{line, proxy, reason}],
    // errors: [{line, proxy, errors: {поле: [тексты]}}]}. Проверку не запускает.
    // proxy_group_id — одна группа на всю пачку (§8.3); не выбрана — поле не шлём (без группы).
    static Import({ list, is_active, proxy_group_id, mode }) {
        const api = new this();
        api.__type = 'store';
        api.paths = { 'store': 'authorized/admin/proxies/import' };
        api.__params = {
            list,
            is_active,
            ...((proxy_group_id !== undefined && proxy_group_id !== null && proxy_group_id !== '')
                ? { proxy_group_id: Number(proxy_group_id) } : {}),
            // Режим всей пачки; не выбран — не шлём: режим каждого определит его первая проверка.
            ...(mode ? { mode } : {}),
        };
        api.__extraKeys = ['skipped', 'errors'];
        return api;
    }

    // Проверка (POST proxies/check) — и одного, и пачки: ставит в очередь и отвечает сразу,
    // проверяет крон пачками, итог — в last_check при следующем чтении. Синхронный
    // POST proxies/{id}/check не используем: он держит запрос до ~4 мин.
    // Ответ — {data: [прокси со status queued|running], missing: [id удалённых/несуществующих]}.
    static CheckMany(ids) {
        const api = new this();
        api.__type = 'store';
        api.paths = { 'store': 'authorized/admin/proxies/check' };
        api.__params = { ids };
        api.__extraKeys = ['missing'];
        return api;
    }

    // Импорт и массовая проверка кладут рядом с data свои списки (skipped/errors, missing), а
    // базовый разбор оставил бы только data — отдаём ответ целиком, записи — через форматтер.
    parseResponse(response) {
        if (!this.__extraKeys || !response) return super.parseResponse(response);
        const result = { data: Array.isArray(response.data) ? response.data.map(this.formatterItem) : [] };
        this.__extraKeys.forEach((key) => { result[key] = response[key] ?? []; });
        return result;
    }

    // Массовый замер (POST proxies/probe): ставит выбранные в очередь замера и отвечает сразу, меряет
    // крон. Ответ — как у массовой проверки: {data: [прокси с concurrency.status queued|running],
    // missing: [id удалённых/несуществующих]}.
    static ProbeMany(ids) {
        const api = new this();
        api.__type = 'store';
        api.paths = { 'store': 'authorized/admin/proxies/probe' };
        api.__params = { ids };
        api.__extraKeys = ['missing'];
        return api;
    }

    // Замер одновременных соединений (POST proxies/{id}/probe): в очередь, ответ — прокси
    // (короткий вид, как в списке) с concurrency.status queued (или running, если замер уже идёт — второй не ставится).
    static Probe(id) {
        const api = new this();
        api.__type = 'store';
        api.paths = { 'store': `authorized/admin/proxies/${id}/probe` };
        api.__params = {};
        return api;
    }
}
